import { STATS_GRID_COLUMN_NUMBER } from "../../../../../utils/Constants";

export type FrequencyEntry = {
  number: string;
  frequency: number;
};

export interface FrequencyChartProps {
  data: FrequencyEntry[];
  yLabel?: string;
}

export default function FrequencyChart(props: FrequencyChartProps) {
  let dataMax: number = props.data[0]?.frequency ?? 0;
  let dataMin: number = props.data[0]?.frequency ?? 0;
  props.data.forEach((value) => {
    if (value.frequency > dataMax) {
      dataMax = value.frequency;
    }
    if (value.frequency < dataMin) {
      dataMin = value.frequency;
    }
  });
  const chartMax = findChartMax(dataMax);

  /**
     * @description Based on dataMax, find a chartMax, that:
            1) is multiple of half of the largest power of 10 beneath dataMax
     * @param dataMax Maximum value of the chart data
     * @returns The chart maximum measured value (chartMax)
     */
  function findChartMax(dataMax: number): number {
    if (!isFinite(dataMax) || isNaN(dataMax) || dataMax <= 0) return 1;
    // Step 1: Find the largest power of 10 beneath dataMax
    const largestPowerOfTen = Math.pow(10, Math.floor(Math.log10(dataMax)));
    if (!isFinite(largestPowerOfTen) || isNaN(largestPowerOfTen) || largestPowerOfTen <= 0) return Math.ceil(dataMax) || 1;
    // Step 2: Return the next multiple of the largest power of 10
    const result = Math.ceil(dataMax / (largestPowerOfTen / 2)) * (largestPowerOfTen / 2);
    if (!isFinite(result) || isNaN(result) || result <= 0) return Math.ceil(dataMax) || 1;
    return result;
  }
  return (
    <div className="w-full h-fit flex flex-col items-center">
      <div className="w-full flex flex-row justify-between items-center pl-4.5">
        <div
          className={`select-none mb-1 font-archb ~text-xs/sm ~leading-3/4 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor text-start`}
        >
          0
        </div>
        <div
          className={`select-none mb-1 font-archb ~text-xs/sm ~leading-3/4 bg-clip-text text-transparent bg-gradient-to-br from-white to-emperor text-end`}
        >
          {chartMax}
        </div>
      </div>
      <div className="w-full h-fit flex flex-row">
        <div className="flex flex-col pt-1.5 pb-1 pr-1 items-end">
          {props.data.map((value) => (
            <span
              key={value.number}
              className={
                `select-none flex grow cursor-default hover:scale-110 font-archb ~text-xs/sm leading-2 bg-clip-text text-transparent bg-gradient-to-br ` +
                (value.frequency == dataMax
                  ? "from-mango to-crail"
                  : value.frequency == dataMin
                    ? "from-gray to-emperor"
                    : "from-white to-emperor")
              }
              data-tooltip-id="default-tooltip"
              data-tooltip-class-name="z-20"
              data-tooltip-content={`${props.yLabel} ${value.number} was drawn ${value.frequency} times`}
            >
              {value.number}
            </span>
          ))}
        </div>
        <div className={`relative w-full h-fit`}>
          <div className="py-1.5 px-[1px] h-fit">
            {props.data.map((value, index) => (
              <div
                key={value.number}
                style={{
                  width: `${Math.floor((value.frequency / chartMax) * 100)}%`,
                }}
                className={`select-none hover:scale-y-125 relative z-20 h-[11px] ${index >= props.data.length - 1 ? "" : "mb-0.5"} bg-gradient-to-bl ${value.frequency == dataMax ? "from-mango to-crail" : value.frequency == dataMin ? "from-emperor to-black" : "from-white to-emperor"}`}
                data-tooltip-id="default-tooltip"
                data-tooltip-class-name="z-20"
                data-tooltip-content={`${props.yLabel} ${value.number} was drawn ${value.frequency} times`}
              />
            ))}
          </div>
          <div className="absolute inset-0 z-10 flex flex-row opacity-50">
            {Array(STATS_GRID_COLUMN_NUMBER - 1)
              .fill(null)
              .map((_, index) => (
                <div
                  key={index}
                  className="flex grow h-full border-l border-t border-b border-persian-pink"
                ></div>
              ))}
            <div className="flex grow h-full border-l border-t border-b border-r border-persian-pink"></div>
          </div>
          <div className="absolute inset-0 z-10 flex flex-col opacity-25">
            {Array(props.data.length)
              .fill(null)
              .map((_, index) => (
                <div
                  key={index}
                  className="flex grow w-full border-l border-t border-r border-persian-pink"
                ></div>
              ))}
            <div className="flex grow w-full border-l border-t border-b border-r border-persian-pink"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
